import {
    IdentityBodyAssessMethod,
    IdentityBodyAssessReading,
    IdentityDailyWeightLoss,
    IdentityMeasurementUnit,
    IdentityPerson,
    IdentitySeason,
} from '@reg/shared/model';
import { IdentityBodyAssessService } from '@reg/shared/service';
import { PercentPipe } from '@angular/common';
import { DateFormat } from '@reg/shared/enum';
import { format } from 'date-fns';
import { convertToDateTime } from '@reg/shared/helper/dateTime-helper';

/**
 * Model to hold body assessment and it's associated readings data given by an assessor
 */
export class IdentityBodyAssess {
    public id: string = null;
    public person: IdentityPerson = null;
    public season: IdentitySeason = null;
    public weight: number = null;
    public minWeight: number = null;
    public bodyFatPercentage: number = null;
    public minBodyFatPercentage: number = null;
    public bodyAssessmentMethodId: string = null;
    public acceptedBy: IdentityPerson = null;
    public acceptedDateTime: Date = null;
    public assessmentDate: Date = null;
    public isEligible: boolean = null; // only fetched and set if 'isEligible' is provided as a field to return
    public readings: IdentityBodyAssessReading[] = [];
    public weightLossPlan: IdentityDailyWeightLoss[] = [];
    public measurementUnit: IdentityMeasurementUnit;

    public static getFields(): string[] {
        return [
            'person',
            'season',
            'weight',
            'minWeight',
            'bodyFatPercentage',
            'minBodyFatPercentage',
            'measurementUnitId',
            'bodyAssessmentMethodId',
            'acceptedBy',
            'acceptedDateTime',
            'assessmentDate',
            // 'weightLossPlan', This can be expensive so we will only fetch when needed
            IdentityBodyAssessService.INCLUDE_READINGS,
            IdentityBodyAssessService.INCLUDE_SEASON,
        ];
    }

    constructor() {
        this.measurementUnit = IdentityMeasurementUnit.createLbs();
    }

    public formatBodyFatPercentage(): string {
        return this.formatBodyFat(this.bodyFatPercentage);
    }

    public formatMinBodyFatPercentage(): string {
        return this.formatBodyFat(this.minBodyFatPercentage);
    }

    public formatBodyFat(value: number): string {
        if (this.bodyAssessmentMethodId === IdentityBodyAssessMethod.ID_MINIMUM_WEIGHT) {
            return 'N/A';
        }

        if (value) {
            return new PercentPipe('en-US').transform(value, '1.2-2');
        }

        return '';
    }

    public isAccepted(): boolean {
        return !!this.acceptedDateTime;
    }

    /**
     * Clone the person
     * @param {boolean} clonePerson If the parent person reference is calling the clone we don't want to clone it to avoid an infinite loop
     * @returns {IdentityBodyAssess}
     */
    public clone(clonePerson: boolean = true): IdentityBodyAssess {
        const clone: IdentityBodyAssess = new IdentityBodyAssess();
        clone.id = this.id;
        clone.weight = this.weight;
        clone.minWeight = this.minWeight;
        clone.bodyFatPercentage = this.bodyFatPercentage;
        clone.minBodyFatPercentage = this.minBodyFatPercentage;
        clone.bodyAssessmentMethodId = this.bodyAssessmentMethodId;
        clone.isEligible = this.isEligible;

        if (this.acceptedDateTime) {
            clone.acceptedDateTime = new Date(this.acceptedDateTime.getTime());
        }

        if (this.measurementUnit) {
            clone.measurementUnit = this.measurementUnit.clone();
        }

        if (this.season) {
            clone.season = this.season.clone();
        }

        if (clonePerson && this.person) {
            clone.person = this.person.clone();
        }

        if (this.acceptedBy) {
            clone.acceptedBy = this.acceptedBy.clone();
        }

        if (this.assessmentDate) {
            clone.assessmentDate = new Date(this.assessmentDate.getTime());
        }

        if (this.readings) {
            clone.readings = this.readings.map((r: IdentityBodyAssessReading) => r.clone());
        }

        if (this.weightLossPlan) {
            clone.weightLossPlan = this.weightLossPlan.map((w: IdentityDailyWeightLoss) => w.clone());
        }

        return clone;
    }

    /**
     * Serialize to a json api format
     * @returns {string}
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'bodyAssessment';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.relationships = {};

        json.data.attributes.weight = parseFloat(this.weight.toString());
        json.data.attributes.assessmentDate = format(this.assessmentDate, DateFormat.YYYYMMDD);

        if (this.person) {
            json.data.relationships.person = {
                data: { type: 'person', id: this.person.id },
            };
        }
        if (this.season) {
            json.data.relationships.season = {
                data: { type: 'season', id: this.season.id },
            };
        }
        if (this.bodyAssessmentMethodId) {
            json.data.relationships.bodyAssessmentMethod = {
                data: { type: 'bodyAssessmentMethod', id: this.bodyAssessmentMethodId },
            };
        }
        if (this.acceptedBy) {
            json.data.relationships.acceptedBy = {
                data: { type: 'person', id: this.acceptedBy.id },
            };
        }
        if (this.measurementUnit) {
            json.data.relationships.measurementUnit = {
                data: { type: 'measurementUnit', id: this.measurementUnit.id },
            };
        }

        return JSON.stringify(json);
    }

    /**
     * Deserialize to json api format
     */
    public deserialize(json: Object, included: Object[] = null): IdentityBodyAssess {
        const data: Object = json['data'] || json;
        const attrs: Object = data['attributes'];
        included = included || json['included'];
        const relationships: Object[] = data['relationships'] || [];

        this.id = data['id'];
        this.weight = attrs['weight'];
        this.minWeight = attrs['minWeight'];
        this.bodyFatPercentage = attrs['bodyFatPercentage'];
        this.minBodyFatPercentage = attrs['minBodyFatPercentage'];
        this.bodyAssessmentMethodId = attrs['bodyAssessmentMethodId'];
        this.isEligible = attrs['isEligible'];
        this.assessmentDate = convertToDateTime(attrs['assessmentDate']);

        if (attrs['acceptedDateTime']) {
            this.acceptedDateTime = new Date(attrs['acceptedDateTime']);
        }

        const dailyWeightLoss: Object[] = attrs['weightLossPlan'] || [];
        dailyWeightLoss.forEach((w: any) => {
            this.weightLossPlan.push(new IdentityDailyWeightLoss().deserialize(w));
        });

        const personLink: Object = relationships['person'];
        if (personLink &&
            included &&
            personLink['data'] &&
            personLink['data']['id']) {
            const personId: string = personLink['data']['id'];
            const persons: Object[] =
                included.filter((i: Object) => i['type'] === 'person' && i['id'] === personId);

            if (persons.length === 1) {
                this.person = (new IdentityPerson()).deserialize(persons[0], included, '');
            }
        } else if (attrs['personId']) {
            this.person = new IdentityPerson();
            this.person.id = attrs['personId'];
        }

        const seasonLink: Object = relationships['season'];
        if (seasonLink &&
            included &&
            seasonLink['data'] &&
            seasonLink['data']['id']) {
            const seasonId: string = seasonLink['data']['id'];
            const seasons: Object[] =
                included.filter((i: Object) => i['type'] === 'season' && i['id'] === seasonId);

            if (seasons.length === 1) {
                this.season = (new IdentitySeason()).deserialize(seasons[0], included);
            }
        } else if (attrs['seasonId']) {
            this.season = new IdentitySeason();
            this.season.id = attrs['seasonId'];
        }

        const acceptedByLink: Object = relationships['acceptedBy'];
        if (acceptedByLink &&
            included &&
            acceptedByLink['data'] &&
            acceptedByLink['data']['id']) {
            const acceptedById: string = acceptedByLink['data']['id'];
            const acceptedBys: Object[] =
                included.filter((i: Object) => i['type'] === 'person' && i['id'] === acceptedById);

            if (acceptedBys.length === 1) {
                this.acceptedBy = (new IdentityPerson()).deserialize(acceptedBys[0], included, '');
            }
        } else if (attrs['acceptedById']) {
            this.acceptedBy = new IdentityPerson();
            this.acceptedBy.id = attrs['acceptedById'];
        }

        const measurementUnitLink: Object = relationships['measurementUnit'];
        if (measurementUnitLink &&
            included &&
            measurementUnitLink['data'] &&
            measurementUnitLink['data']['id']) {
            const measurementUnitId: string = measurementUnitLink['data']['id'];
            const measurementUnits: Object[] =
                included.filter((i: Object) => i['type'] === 'measurementUnit' && i['id'] === measurementUnitId);

            if (measurementUnits.length === 1) {
                this.measurementUnit = (new IdentityMeasurementUnit()).deserialize(measurementUnits[0]);
            }
        } else if (attrs['measurementUnitId']) {
            this.measurementUnit = new IdentityMeasurementUnit(attrs['measurementUnitId']);
        }

        const readingLinks: Object = relationships['readings'];
        if (readingLinks &&
            included &&
            readingLinks['data']) {
            this.readings = [];

            for (const readingLink of readingLinks['data']) {
                const reading: Object = included.filter((i: Object) => i['id'] === readingLink['id'])[0];
                if (reading) {
                    this.readings.push((new IdentityBodyAssessReading()).deserialize(reading, included));
                }
            }
        }

        return this;
    }
}
